import Api from "services/Api";
import _ from 'lodash';
import store from 'store/index';
import Validator from 'services/Validator';
import {previousValid} from 'store/generators/step';
import {requestPayload} from 'store/modules/claim';

/**
 * Mutation Types
 * 
 * @type {Object}
 */
const mutationTypes = {
    RESET: 'RESET',
    FINISHED_REFRESH: 'FINISHED_REFRESH',
    UPDATE_DATA: 'UPDATE_DATA',
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER'
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'assumedLossTotal' : {type: 'number', rules: 'required'},
    'futureLossTotal' : {type: 'number', rules: 'required'},
};

function initialState() {
    return {
        pastLossPeriods: null,
        pastLossDuration: null,
        pastLossMultiplier: null,
        pastNetLossTotal: null,
        assumedLossTotal: null,
        assumedLossMultiplier: null,
        futureLossPeriods: null,
        futureLossDuration: null,
        futureLossMultiplier: null,
        futureLossTotal: null,
    };
}

/**
 * Initial State
 * 
 * @type {Object}
 */
const state = initialState();

/**
 * Getters.
 * 
 * @type {Object}
 */
const getters = {
    pastLossPeriods: state => state.pastLossPeriods,
    pastLossDuration: state => state.pastLossDuration,
    pastLossMultiplier: state => state.pastLossMultiplier,
    pastNetLossTotal: state => state.pastNetLossTotal,
    assumedLossTotal: state => state.assumedLossTotal,
    assumedLossMultiplier: state => state.assumedLossMultiplier,
    futureLossPeriods: state => state.futureLossPeriods,
    futureLossDuration: state => state.futureLossDuration,
    futureLossMultiplier: state => state.futureLossMultiplier,
    futureLossTotal: state => state.futureLossTotal,
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     */
    save({getters, dispatch, state}, payload) {
        // We might change this to something which 
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });

        store.dispatch('personal/save', requestPayload(5));
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     */
    load({ dispatch, action, commit }, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.UPDATE_FROM_SERVER, { data });
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({ commit, dispatch }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: true, // needs updating with real validity
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({ commit, rootState }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: previousValid(rootState, 4)
            && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     */
    reset({ commit }) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 4,
            valid: false,
        });
        
        commit(mutationTypes.RESET);
    },

    /**
     * Set the pastLossOfEarnings head of loss for later calculations
     */
    updatePastLossOfEarnings({ rootState }, duration) {
        let netIncomePerAnnum = rootState.personal.claimant.theClaimant.income.pastPeriods[0].totalNetIncome;
        let totalPastNetIncomeLoss = (netIncomePerAnnum * duration) - state.pastNetLossTotal;

        store.dispatch('personal/headsOfLoss/setHeadsOfLossValue', {key: 'pastLossOfEarnings',value: totalPastNetIncomeLoss});
    },

    /**
     * Refresh the step.
     *
     * An opportunity to perform recalculations. Prevents out of date calculations
     * from being displayed to a user. 
     *
     * Note: This must be called manually in a vue (this.refresh() after mapping the actions)
     * Once we have some time to refactor, I'll watch the step change which occurs in
     * mutations.js and trigger the refresh() on every step change. 
     */
    refresh({ commit, dispatch, getters, rootState }, payload) {
        Api.calculators.monetary.postInjuryLossOfIncomeTables(
                rootState.personal.claimant.theClaimant,
                rootState.personal.claimant.claimInfo
            ).then(data => {
                // snake case data here first!
                commit(mutationTypes.UPDATE_DATA, { data });
                commit(mutationTypes.FINISHED_REFRESH);
                dispatch('updatePastLossOfEarnings', data.past_loss_duration);
            });
    },
};

// Mutations
const mutations = {
    /**
     * Reset state.
     */
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
    /**
     * Update Data.
     */
    [mutationTypes.UPDATE_DATA](state, { data }) {
        state.pastLossPeriods = data.past_loss_periods;
        state.pastLossDuration = data.past_loss_duration;
        state.pastLossMultiplier = data.past_loss_multiplier;
        state.assumedLossTotal = data.assumed_loss_total;
        state.pastNetLossTotal = data.past_net_loss_total;
        state.assumedLossMultiplier = data.assumed_loss_multiplier;
        state.futureLossPeriods = data.future_loss_periods;
        state.futureLossDuration = data.future_loss_duration;
        state.futureLossMultiplier = data.future_loss_multiplier;
        state.futureLossTotal = data.future_loss_total;
    },
    /**
     * Update from server.
     */
    [mutationTypes.UPDATE_FROM_SERVER](state, { data }) {
        state.pastLossPeriods = data.pastLossPeriods;
        state.pastLossDuration = data.pastLossDuration;
        state.pastLossMultiplier = data.pastLossMultiplier;
        state.pastNetLossTotal = data.pastNetLossTotal;
        state.assumedLossTotal = data.assumedLossTotal;
        state.assumedLossMultiplier = data.assumedLossMultiplier;
        state.futureLossPeriods = data.futureLossPeriods;
        state.futureLossDuration = data.futureLossDuration;
        state.futureLossMultiplier = data.futureLossMultiplier;
        state.futureLossTotal = data.futureLossTotal;
    },
    /**
     * Finished Refresh.
     */
    [mutationTypes.FINISHED_REFRESH]() {

    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/personal/lossOfIncome.js